/* verqo — MegaMenu (shared, marketing site)
   Ramp-style 4-column mega-menu. No highlight card (per user spec).
   Renders columns of items with icons + one-line descriptions.

   Hover behavior (Feb 2026 fix):
     - Panel is positioned with an invisible padding-top bridge so the mouse
       can traverse from tab -> panel without crossing dead space.
     - Mouseleave uses a short delay (180ms) so brief off-track hovers do not
       collapse the panel.
     - Keyboard-accessible: opens on focus, closes on Escape / outside click.
*/
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown } from "lucide-react";

export function MegaMenu({ items }) {
  const [openId, setOpenId] = useState(null);
  const wrapRef = useRef(null);
  const closeTimerRef = useRef(null);

  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpenId(null), 180);
  };
  const cancelClose = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const openNow = (id) => {
    cancelClose();
    setOpenId(id);
  };

  useEffect(() => {
    if (!openId) return;
    const onKey = (e) => { if (e.key === "Escape") setOpenId(null); };
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpenId(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [openId]);

  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  }, []);

  return (
    <nav
      ref={wrapRef}
      className="hidden lg:flex items-stretch gap-1 text-[15px] text-slate-700"
      onMouseLeave={scheduleClose}
      data-testid="mega-nav"
    >
      {items.map((tab) => {
        const isOpen = openId === tab.id;
        // Simple link tabs (no mega)
        if (!tab.mega) {
          return (
            <Link
              key={tab.id}
              to={tab.href}
              className="px-4 py-2.5 rounded-md hover:bg-slate-100/70 hover:text-slate-900 transition-colors font-medium"
              data-testid={`nav-tab-${tab.id}`}
            >
              {tab.label}
            </Link>
          );
        }
        return (
          <div
            key={tab.id}
            className="relative"
            onMouseEnter={() => openNow(tab.id)}
          >
            <button
              type="button"
              onClick={() => (isOpen ? setOpenId(null) : openNow(tab.id))}
              onFocus={() => openNow(tab.id)}
              aria-expanded={isOpen}
              aria-haspopup="true"
              data-testid={`nav-tab-${tab.id}`}
              className={`inline-flex items-center gap-1.5 px-4 py-2.5 rounded-md hover:bg-slate-100/70 hover:text-slate-900 transition-colors font-medium ${
                isOpen ? "text-slate-900" : ""
              }`}
            >
              {tab.label}
              <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? "rotate-180" : ""}`} />
            </button>

            {/* Mega panel — wrapper has invisible top padding acting as a hover
                bridge so the mouse can travel from the button down into the
                panel card without crossing dead space. */}
            {isOpen && (
              <div
                className="fixed left-1/2 -translate-x-1/2 top-[66px] pt-4 w-[min(1200px,calc(100vw-32px))] z-50"
                data-testid={`mega-panel-${tab.id}`}
                onMouseEnter={cancelClose}
                onMouseLeave={scheduleClose}
              >
                <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_32px_80px_-24px_rgba(15,23,42,0.28)] overflow-hidden">
                  <div className="flex">
                  <div className="flex-1 grid gap-x-6 gap-y-8 p-8" style={{ gridTemplateColumns: `repeat(${tab.columns.length}, minmax(0, 1fr))` }}>
                    {tab.columns.map((col) => (
                      <div key={col.heading}>
                        <div className="text-[10.5px] uppercase tracking-[0.24em] text-slate-500 font-semibold mb-4">
                          {col.heading}
                        </div>
                        <ul className="space-y-3">
                          {col.items.map((it) => (
                            <li key={it.href}>
                              <Link
                                to={it.href}
                                onClick={() => setOpenId(null)}
                                data-testid={`nav-item-${tab.id}-${it.slug}`}
                                className="group flex items-start gap-3 -mx-2 px-2 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                              >
                                {it.icon && (
                                  <span className="mt-0.5 flex-shrink-0 w-8 h-8 rounded-lg bg-slate-100 group-hover:bg-lime-100 grid place-items-center transition-colors">
                                    <it.icon className="w-4 h-4 text-slate-700 group-hover:text-lime-800" strokeWidth={1.8} />
                                  </span>
                                )}
                                <span className="min-w-0">
                                  <span className="block text-[13.5px] font-semibold text-slate-900 leading-tight">
                                    {it.label}
                                  </span>
                                  <span className="mt-0.5 block text-[12px] text-slate-500 leading-snug">
                                    {it.desc}
                                  </span>
                                </span>
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                  {/* FEATURED column — Ramp-style highlight rail */}
                  {tab.featured && (
                    <aside className="hidden lg:block w-[290px] flex-shrink-0 border-l border-slate-200 bg-slate-50/70 p-7">
                      <div className="text-[10.5px] uppercase tracking-[0.24em] text-slate-500 font-semibold mb-5">Featured</div>
                      <div className="space-y-5">
                        {tab.featured.map((f) => (
                          <Link
                            key={f.href}
                            to={f.href}
                            onClick={() => setOpenId(null)}
                            data-testid={`nav-featured-${tab.id}-${f.slug}`}
                            className="group block rounded-xl border border-slate-200 bg-white p-4 hover:border-lime-400 hover:shadow-[0_16px_40px_-16px_rgba(15,23,42,0.18)] transition-all"
                          >
                            <div className="w-9 h-9 rounded-lg grid place-items-center mb-3" style={{ background: "#0c1612" }}>
                              {f.icon && <f.icon className="w-4.5 h-4.5 text-lime-300" strokeWidth={1.8} />}
                            </div>
                            {f.tag && <div className="text-[9.5px] uppercase tracking-[0.2em] text-lime-700 font-semibold mb-1">{f.tag}</div>}
                            <div className="text-[14px] font-semibold text-slate-900 leading-tight group-hover:text-lime-800 transition-colors">{f.label}</div>
                            <div className="mt-1 text-[12px] text-slate-500 leading-snug">{f.desc}</div>
                          </Link>
                        ))}
                      </div>
                    </aside>
                  )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </nav>
  );
}
